import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { ClipboardList, ExternalLink, AlertTriangle, CheckCircle2 } from "lucide-react";
import Breadcrumbs from "@/components/Breadcrumbs";
import { FORM_GUIDES, getFormGuide } from "@/data/formGuides";

const SITE = "https://hireds.in";

export function GuidesIndex() {
  useEffect(() => {
    const oldTitle = document.title;
    document.title = "Govt Exam Application Form Guides 2026 — Step-by-Step | hireds.in";
    const md = document.querySelector('meta[name="description"]');
    const oldMd = md ? md.getAttribute("content") : null;
    if (md) md.setAttribute("content", "Step-by-step apply-online guides for SSC CGL, RRB NTPC, UPSC CSE, IBPS PO, SBI PO, RRB ALP and more. Avoid common application form mistakes.");
    const canon = document.querySelector('link[rel="canonical"]');
    const oldCanon = canon ? canon.getAttribute("href") : null;
    if (canon) canon.setAttribute("href", `${SITE}/guides`);
    return () => {
      document.title = oldTitle;
      if (md && oldMd != null) md.setAttribute("content", oldMd);
      if (canon && oldCanon != null) canon.setAttribute("href", oldCanon);
    };
  }, []);

  return (
    <div className="max-w-5xl mx-auto px-4 md:px-6 py-6" data-testid="guides-index">
      <Breadcrumbs items={[{ name: "Home", url: "/" }, { name: "Form Guides", url: "/guides" }]} />
      <header className="mb-7">
        <p className="text-xs font-bold uppercase tracking-wider text-orange-600">Application Guides</p>
        <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900 mt-1" style={{ fontFamily: "'Cabinet Grotesk',sans-serif" }}>
          Govt Exam Application Form Guides — Step by Step
        </h1>
        <p className="text-base text-slate-700 mt-2 leading-relaxed">
          Filling the form wrong is the #1 reason applications get rejected. Every guide below walks you through the exact steps, photo/signature dimensions, fee structure, and the top 3-4 mistakes candidates make.
        </p>
      </header>

      <ul className="grid sm:grid-cols-2 gap-4">
        {FORM_GUIDES.map((g) => (
          <li key={g.slug}>
            <Link
              to={`/guides/${g.slug}`}
              data-testid={`guide-card-${g.slug}`}
              className="block h-full p-5 rounded-xl border border-slate-200 bg-white hover:border-orange-400 hover:shadow-md transition"
            >
              <div className="flex items-start gap-3">
                <span className="inline-grid place-items-center w-9 h-9 rounded-md bg-orange-100 text-orange-700 shrink-0">
                  <ClipboardList className="w-4 h-4" aria-hidden="true" />
                </span>
                <div>
                  <h2 className="text-base font-extrabold text-slate-900 leading-snug" style={{ fontFamily: "'Cabinet Grotesk',sans-serif" }}>
                    {g.title}
                  </h2>
                  <p className="text-xs text-slate-500 mt-1">Fee (Gen): {g.fee_general} · Reserved: {g.fee_reserved}</p>
                </div>
              </div>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function GuideDetail() {
  const { slug } = useParams();
  const guide = getFormGuide(slug);

  useEffect(() => {
    if (!guide) return;
    const oldTitle = document.title;
    document.title = `${guide.title} | hireds.in`;
    const md = document.querySelector('meta[name="description"]');
    const oldMd = md ? md.getAttribute("content") : null;
    if (md) md.setAttribute("content", `Apply for ${guide.exam} the right way — step-by-step form filling guide, photo/signature size, application fees, and the most common rejection mistakes to avoid.`);
    const canon = document.querySelector('link[rel="canonical"]');
    const oldCanon = canon ? canon.getAttribute("href") : null;
    if (canon) canon.setAttribute("href", `${SITE}/guides/${guide.slug}`);
    return () => {
      document.title = oldTitle;
      if (md && oldMd != null) md.setAttribute("content", oldMd);
      if (canon && oldCanon != null) canon.setAttribute("href", oldCanon);
    };
  }, [guide]);

  if (!guide) {
    return (
      <div className="max-w-3xl mx-auto px-4 md:px-6 py-12 text-center" data-testid="guide-not-found">
        <h1 className="text-2xl font-extrabold text-slate-900">Guide not found</h1>
        <p className="text-sm text-slate-600 mt-2">
          Browse <Link to="/guides" className="text-orange-600 underline">all form-fill guides</Link>.
        </p>
      </div>
    );
  }

  return (
    <article className="max-w-3xl mx-auto px-4 md:px-6 py-6" data-testid="guide-detail">
      <Breadcrumbs items={[
        { name: "Home", url: "/" },
        { name: "Form Guides", url: "/guides" },
        { name: guide.exam, url: `/guides/${guide.slug}` },
      ]} />
      <header className="mb-6">
        <p className="text-xs font-bold uppercase tracking-wider text-orange-600">Form-fill Guide</p>
        <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900 mt-1" style={{ fontFamily: "'Cabinet Grotesk',sans-serif" }}>
          {guide.title}
        </h1>
        <div className="flex flex-wrap gap-3 mt-3 text-sm">
          <span className="px-2.5 py-1 rounded-md bg-slate-900 text-white font-semibold">Fee — General: {guide.fee_general}</span>
          <span className="px-2.5 py-1 rounded-md bg-green-100 text-green-900 font-semibold">Reserved: {guide.fee_reserved}</span>
          <a href={guide.portal} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-slate-300 text-slate-800 hover:border-orange-400">
            Official portal <ExternalLink className="w-3.5 h-3.5" aria-hidden="true" />
          </a>
        </div>
      </header>

      <section className="mb-7">
        <h2 className="text-xl font-extrabold text-slate-900 mb-3" style={{ fontFamily: "'Cabinet Grotesk',sans-serif" }}>Step-by-step application process</h2>
        <ol className="space-y-2 list-decimal list-outside ml-6 text-slate-800 leading-relaxed">
          {guide.steps.map((s, i) => (
            <li key={i} className="text-base">{s}</li>
          ))}
        </ol>
      </section>

      <section className="mb-7 rounded-xl border border-amber-200 bg-amber-50 p-5">
        <h2 className="text-xl font-extrabold text-amber-900 mb-3 flex items-center gap-2" style={{ fontFamily: "'Cabinet Grotesk',sans-serif" }}>
          <AlertTriangle className="w-5 h-5" aria-hidden="true" /> Top mistakes that get applications rejected
        </h2>
        <ul className="space-y-2 list-disc list-outside ml-6 text-slate-800 leading-relaxed">
          {guide.common_mistakes.map((m, i) => (
            <li key={i} className="text-base">{m}</li>
          ))}
        </ul>
      </section>

      <section className="mb-7 rounded-xl border border-green-200 bg-green-50 p-5">
        <h2 className="text-xl font-extrabold text-green-900 mb-2 flex items-center gap-2" style={{ fontFamily: "'Cabinet Grotesk',sans-serif" }}>
          <CheckCircle2 className="w-5 h-5" aria-hidden="true" /> Last-minute tip
        </h2>
        <p className="text-base text-slate-800 leading-relaxed">{guide.last_minute}</p>
      </section>

      <p className="text-xs text-slate-500 leading-relaxed">
        Disclaimer: hireds.in is an independent editorial portal. Always verify the latest dates and fee structure on the{" "}
        <a href={guide.portal} target="_blank" rel="noopener noreferrer" className="underline text-orange-600">official portal</a> before applying.
      </p>
    </article>
  );
}
